import { z } from 'zod';
import { VALUATION_AGENT_PERMISSIONS } from '../permissions';

// The valuation agent's token — the `vag_` bearer an operator issues so the agent can reach immoOS (IMM-1402).
// Managed from `admin.immoos.ai` under `/v1/admin/valuation-agent/tokens`. Bound to no workspace and no member.

export const ValuationAgentPermissionSchema = z.enum(VALUATION_AGENT_PERMISSIONS);

// The read never shows a REVOKED token or a replaced one past its 24 hours.
export const ValuationAgentTokenStatusSchema = z.enum(['ACTIVE', 'REPLACED']);
export type ValuationAgentTokenStatus = z.infer<typeof ValuationAgentTokenStatusSchema>;

export const ValuationAgentTokenViewSchema = z.object({
  id: z.uuid(),
  status: ValuationAgentTokenStatusSchema,
  // VALUATION_AGENT_PERMISSIONS order.
  permissions: z.array(ValuationAgentPermissionSchema),
  issuedAt: z.iso.datetime(),
  // name is null when the operator no longer resolves.
  issuedBy: z.object({ adminId: z.uuid(), name: z.string().nullable() }),
  lastUsedAt: z.iso.datetime().nullable(),
  // Non-null iff REPLACED.
  validUntil: z.iso.datetime().nullable(),
});
export type ValuationAgentTokenView = z.infer<typeof ValuationAgentTokenViewSchema>;

export const ValuationAgentTokensResponseSchema = z.object({
  active: ValuationAgentTokenViewSchema.nullable(),
  // The replaced token while its 24-hour overlap runs; null otherwise.
  replaced: ValuationAgentTokenViewSchema.nullable(),
});
export type ValuationAgentTokensResponse = z.infer<typeof ValuationAgentTokensResponseSchema>;

// No `.min(1)` on purpose: the empty set must reach the domain so the refusal carries its own code and copy.
export const IssueValuationAgentTokenBodySchema = z
  .object({ permissions: z.array(ValuationAgentPermissionSchema) })
  .strict();
export type IssueValuationAgentTokenBody = z.infer<typeof IssueValuationAgentTokenBodySchema>;

export const IssuedValuationAgentTokenResponseSchema = z.object({
  token: ValuationAgentTokenViewSchema,
  secret: z.string(),
});
export type IssuedValuationAgentTokenResponse = z.infer<typeof IssuedValuationAgentTokenResponseSchema>;
